import { useState } from "react";
import { Plus, Wallet, Pencil, Trash2 } from "lucide-react";
import { useCollection } from "@/hooks/useCollection";
import { useWedding } from "@/context/WeddingContext";
import { formatMoney } from "@/lib/format";
import { PageHeader, StatCard, Progress, EmptyState, PageSkeleton } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

const STATUSES = ["Unpaid", "Deposit Paid", "Partially Paid", "Paid", "Overdue"];
const SPENT_STATUSES = ["Paid", "Partially Paid", "Deposit Paid"];

export default function Budget() {
  const { wedding } = useWedding();
  const cur = wedding?.currency || "AUD";
  const cats = useCollection("budget_categories");
  const txns = useCollection("transactions");

  const [catOpen, setCatOpen] = useState(false);
  const [catForm, setCatForm] = useState({ name: "", planned: "" });
  const [editCat, setEditCat] = useState(null);
  const [txOpen, setTxOpen] = useState(false);
  const [txForm, setTxForm] = useState({});
  const [editTx, setEditTx] = useState(null);

  if (cats.loading || txns.loading) return <PageSkeleton />;

  const spentByCat = {};
  txns.items.forEach((t) => {
    if (SPENT_STATUSES.includes(t.status)) spentByCat[t.category] = (spentByCat[t.category] || 0) + Number(t.amount || 0);
  });
  const totalPlanned = cats.items.reduce((s, c) => s + Number(c.planned || 0), 0);
  const totalBudget = Number(wedding?.budget || 0) || totalPlanned;
  const totalSpent = txns.items.filter((t) => SPENT_STATUSES.includes(t.status)).reduce((s, t) => s + Number(t.amount || 0), 0);
  const outstanding = txns.items.filter((t) => t.status !== "Paid").reduce((s, t) => s + Number(t.amount || 0), 0);

  const saveCat = async () => {
    const payload = { name: catForm.name.trim(), planned: Number(catForm.planned) || 0 };
    if (!payload.name) return;
    if (editCat) await cats.update(editCat.id, payload); else await cats.create(payload);
    toast.success("Category saved"); setCatOpen(false); setEditCat(null); setCatForm({ name: "", planned: "" });
  };

  const saveTx = async () => {
    const payload = {
      date: txForm.date || new Date().toISOString().slice(0, 10),
      category: txForm.category || "Miscellaneous",
      vendor: txForm.vendor || "", description: txForm.description || "",
      amount: Number(txForm.amount) || 0, status: txForm.status || "Unpaid",
      method: txForm.method || "", due_date: txForm.due_date || "", notes: txForm.notes || "",
    };
    if (editTx) await txns.update(editTx.id, payload); else await txns.create(payload);
    toast.success("Expense saved"); setTxOpen(false); setEditTx(null); setTxForm({});
  };

  const catNames = cats.items.map((c) => c.name);

  return (
    <div>
      <PageHeader title="Budget" subtitle="Plan every category and track expenses, deposits and balances." testId="budget-header"
        action={<div className="flex gap-2">
          <Button variant="outline" onClick={() => { setEditCat(null); setCatForm({ name: "", planned: "" }); setCatOpen(true); }} className="rounded-full" data-testid="add-category"><Plus className="h-4 w-4 mr-1.5" />Category</Button>
          <Button onClick={() => { setEditTx(null); setTxForm({}); setTxOpen(true); }} className="rounded-full" data-testid="add-transaction"><Plus className="h-4 w-4 mr-1.5" />Expense</Button>
        </div>} />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="Total budget" value={formatMoney(totalBudget, cur)} icon={Wallet} testId="budget-total" />
        <StatCard label="Planned" value={formatMoney(totalPlanned, cur)} sub={`across ${cats.items.length} categories`} testId="budget-planned" />
        <StatCard label="Spent" value={formatMoney(totalSpent, cur)} accent sub={`${totalBudget ? Math.round(totalSpent / totalBudget * 100) : 0}% of budget`} testId="budget-spent" />
        <StatCard label="Remaining" value={formatMoney(totalBudget - totalSpent, cur)} sub={`${formatMoney(outstanding, cur)} outstanding`} testId="budget-remaining" />
      </div>

      <Tabs defaultValue="categories">
        <TabsList className="rounded-full">
          <TabsTrigger value="categories" className="rounded-full" data-testid="tab-categories">Categories</TabsTrigger>
          <TabsTrigger value="transactions" className="rounded-full" data-testid="tab-transactions">Transactions</TabsTrigger>
        </TabsList>

        <TabsContent value="categories" className="mt-6">
          {cats.items.length === 0 ? (
            <EmptyState icon={Wallet} title="No categories yet" description="Add budget categories to plan your spending." actionLabel="Add category" onAction={() => setCatOpen(true)} testId="cat-empty" />
          ) : (
            <div className="space-y-3">
              {cats.items.map((c) => {
                const spent = spentByCat[c.name] || 0;
                const pct = c.planned ? Math.round(spent / c.planned * 100) : 0;
                return (
                  <div key={c.id} className="rounded-2xl bg-card border border-border p-5" data-testid="category-row">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium">{c.name}</span>
                      <div className="flex items-center gap-3">
                        <span className="text-sm tnum text-muted-foreground">{formatMoney(spent, cur)} / {formatMoney(c.planned, cur)}</span>
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditCat(c); setCatForm({ name: c.name, planned: c.planned }); setCatOpen(true); }} data-testid="edit-category"><Pencil className="h-4 w-4" /></Button>
                        <AlertDialog><AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" data-testid="delete-category"><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                          <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete category?</AlertDialogTitle><AlertDialogDescription>Expenses in this category are kept.</AlertDialogDescription></AlertDialogHeader>
                            <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => { cats.remove(c.id); toast.success("Deleted"); }}>Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>
                      </div>
                    </div>
                    <Progress value={pct} />
                    <div className="text-xs text-muted-foreground mt-1.5 tnum">{pct}% used{pct > 100 ? " · over budget" : ""}</div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        <TabsContent value="transactions" className="mt-6">
          {txns.items.length === 0 ? (
            <EmptyState icon={Wallet} title="No expenses yet" description="Log payments and deposits to track your real spending." actionLabel="Add expense" onAction={() => setTxOpen(true)} testId="tx-empty" />
          ) : (
            <div className="rounded-2xl border border-border bg-card overflow-hidden overflow-x-auto">
              <table className="w-full text-sm">
                <thead><tr className="border-b border-border bg-secondary/40 text-left">
                  {["Date", "Category", "Vendor", "Description", "Amount", "Status", ""].map((h) => <th key={h} className="px-4 py-3 font-medium text-muted-foreground whitespace-nowrap">{h}</th>)}
                </tr></thead>
                <tbody>
                  {txns.items.map((t) => (
                    <tr key={t.id} className="border-b border-border last:border-0 hover:bg-secondary/30" data-testid="transaction-row">
                      <td className="px-4 py-3 whitespace-nowrap">{(t.date || "").slice(0, 10)}</td>
                      <td className="px-4 py-3">{t.category}</td>
                      <td className="px-4 py-3">{t.vendor || "—"}</td>
                      <td className="px-4 py-3">{t.description || "—"}</td>
                      <td className="px-4 py-3 tnum">{formatMoney(t.amount, cur)}</td>
                      <td className="px-4 py-3"><span className={`text-xs px-2.5 py-1 rounded-full ${t.status === "Paid" ? "bg-accent/20 text-accent-foreground" : t.status === "Overdue" ? "bg-destructive/10 text-destructive" : "bg-secondary"}`}>{t.status}</span></td>
                      <td className="px-4 py-3"><div className="flex gap-1 justify-end">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditTx(t); setTxForm({ ...t, date: (t.date || "").slice(0, 10), due_date: (t.due_date || "").slice(0, 10) }); setTxOpen(true); }} data-testid="edit-transaction"><Pencil className="h-4 w-4" /></Button>
                        <AlertDialog><AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" data-testid="delete-transaction"><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                          <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete expense?</AlertDialogTitle></AlertDialogHeader>
                            <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => { txns.remove(t.id); toast.success("Deleted"); }}>Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>
                      </div></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Category dialog */}
      <Dialog open={catOpen} onOpenChange={setCatOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader><DialogTitle className="text-2xl">{editCat ? "Edit category" : "Add category"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="text-xs text-muted-foreground">Name</Label><Input value={catForm.name} onChange={(e) => setCatForm((p) => ({ ...p, name: e.target.value }))} className="rounded-xl mt-1" data-testid="category-name" /></div>
            <div><Label className="text-xs text-muted-foreground">Planned amount ({cur})</Label><Input type="number" value={catForm.planned} onChange={(e) => setCatForm((p) => ({ ...p, planned: e.target.value }))} className="rounded-xl mt-1" data-testid="category-planned" /></div>
          </div>
          <DialogFooter><Button variant="outline" className="rounded-full" onClick={() => setCatOpen(false)}>Cancel</Button><Button className="rounded-full" onClick={saveCat} data-testid="save-category">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transaction dialog */}
      <Dialog open={txOpen} onOpenChange={setTxOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="text-2xl">{editTx ? "Edit expense" : "Add expense"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-2">
            <div><Label className="text-xs text-muted-foreground">Date</Label><Input type="date" value={txForm.date || ""} onChange={(e) => setTxForm((p) => ({ ...p, date: e.target.value }))} className="rounded-xl mt-1" data-testid="tx-date" /></div>
            <div><Label className="text-xs text-muted-foreground">Amount</Label><Input type="number" value={txForm.amount || ""} onChange={(e) => setTxForm((p) => ({ ...p, amount: e.target.value }))} className="rounded-xl mt-1" data-testid="tx-amount" /></div>
            <div><Label className="text-xs text-muted-foreground">Category</Label>
              <Select value={txForm.category || ""} onValueChange={(v) => setTxForm((p) => ({ ...p, category: v }))}><SelectTrigger className="rounded-xl mt-1" data-testid="tx-category"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{catNames.map((n) => <SelectItem key={n} value={n}>{n}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs text-muted-foreground">Status</Label>
              <Select value={txForm.status || "Unpaid"} onValueChange={(v) => setTxForm((p) => ({ ...p, status: v }))}><SelectTrigger className="rounded-xl mt-1" data-testid="tx-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs text-muted-foreground">Vendor</Label><Input value={txForm.vendor || ""} onChange={(e) => setTxForm((p) => ({ ...p, vendor: e.target.value }))} className="rounded-xl mt-1" data-testid="tx-vendor" /></div>
            <div><Label className="text-xs text-muted-foreground">Payment method</Label><Input value={txForm.method || ""} onChange={(e) => setTxForm((p) => ({ ...p, method: e.target.value }))} className="rounded-xl mt-1" /></div>
            <div className="col-span-2"><Label className="text-xs text-muted-foreground">Description</Label><Input value={txForm.description || ""} onChange={(e) => setTxForm((p) => ({ ...p, description: e.target.value }))} className="rounded-xl mt-1" data-testid="tx-description" /></div>
            <div><Label className="text-xs text-muted-foreground">Due date</Label><Input type="date" value={txForm.due_date || ""} onChange={(e) => setTxForm((p) => ({ ...p, due_date: e.target.value }))} className="rounded-xl mt-1" /></div>
            <div className="col-span-2"><Label className="text-xs text-muted-foreground">Notes</Label><Textarea value={txForm.notes || ""} onChange={(e) => setTxForm((p) => ({ ...p, notes: e.target.value }))} className="rounded-xl mt-1" rows={2} /></div>
          </div>
          <DialogFooter><Button variant="outline" className="rounded-full" onClick={() => setTxOpen(false)}>Cancel</Button><Button className="rounded-full" onClick={saveTx} data-testid="save-transaction">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
